import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ScoreBadge from '../components/ScoreBadge';
import {
  Download, FileDown, Loader2,
  Save, CheckCircle, Package, ArrowLeft, Eye, Sparkles, Send, Lock,
} from 'lucide-react';
import { BACKEND_URL } from '../lib/config';

// The AI endpoint returns this text (prefixed) instead of a remark when no provider
// keys are configured. It must never be written into a comment box.
const AI_NOT_CONFIGURED_PREFIX = '[AI remark service';
// Free-tier providers rate-limit, so "Improve all" runs only a couple of calls at once
// and retries a failed call after a short backoff.
const IMPROVE_ALL_CONCURRENCY = 2;
const IMPROVE_RETRY_DELAYS_MS = [2000, 5000];
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Request body for /api/ai/improve-remark: the remark plus its card context
// (section/subsection, the scored parameters the comment refers to, and the other
// comments on the card, from which the backend reads gender consistently).
function buildImprovePayload(text, cell, player, evaluation, cardRemarks) {
  const parameters = (cell?.skills || [])
    .filter(s => s && s.score != null && s.score !== '')
    .map(s => ({ name: s.name, score: s.score }));
  return {
    remark: text,
    category: cell?.section_label || 'other',
    player_name: player?.name || '',
    batch: evaluation?.batch_name || '',
    section: cell?.section_label || '',
    subsection: cell?.type === 'subsection' ? (cell.label || '') : '',
    parameters,
    card_remarks: cardRemarks,
  };
}

// Same request body, built from an /improve-plan item (used by the whole-evaluation run).
function buildBulkPayload(item, playerName, evaluation, cardRemarks) {
  return {
    remark: item.text,
    category: item.section || 'other',
    player_name: playerName || '',
    batch: evaluation?.batch_name || '',
    section: item.section || '',
    subsection: item.subsection || '',
    parameters: (item.parameters || []).map(p => ({ name: p.name, score: p.score })),
    card_remarks: cardRemarks,
  };
}

// Every comment on the card now plus what each held when the card was loaded. The
// backend takes gender only from these, so a card resolves to one answer even after
// some boxes were already rewritten in an impersonal voice.
function collectCardRemarks(original, current) {
  return [...Object.values(original || {}), ...Object.values(current || {})]
    .filter(v => typeof v === 'string' && v.trim());
}

// One improvement request, retried with backoff. Resolves { improved } on success or
// { improved: null, reason } once retries are used up. The backend reports failure as
// ok:false with the coach's original text, never as a rewritten remark.
async function requestImprove(payload, isStale) {
  let reason = '';
  for (let attempt = 0; ; attempt += 1) {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/improve-remark`, payload);
      const improved = res.data?.improved_remark;
      if (res.data?.ok !== false && improved?.trim() && !improved.startsWith(AI_NOT_CONFIGURED_PREFIX)) {
        return { improved };
      }
      reason = res.data?.reason || 'AI service unavailable';
    } catch (e) {
      const detail = e?.response?.data?.detail;
      reason = typeof detail === 'string' ? detail : 'AI request failed';
    }
    if (attempt >= IMPROVE_RETRY_DELAYS_MS.length || isStale() || /not configured/i.test(reason)) {
      return { improved: null, reason };
    }
    await sleep(IMPROVE_RETRY_DELAYS_MS[attempt]);
  }
}

// Readable name of a comment box for the failure message.
function boxLabel(key, cells) {
  if (key === 'additional_comments') return 'Additional Comments';
  const cell = cells.find(c => c.key === key);
  return [cell?.section_label, cell?.type === 'subsection' ? cell.label : '']
    .filter(Boolean).join(' – ') || key;
}

const CATEGORY_LABELS = {
  offence: 'Offence', defence: 'Defence',
  physical_fitness: 'Physical Fitness', auxiliary: 'Auxiliary', other: 'Other',
};

// A4 landscape content area (277mm × 190mm) at 96dpi — used as the natural
// (unscaled) pixel size before the scale-to-fit transform is applied.
const NATURAL_WIDTH = 1047;
const NATURAL_HEIGHT = 718;

/**
 * Build the interactive preview document: the rendered card HTML plus an injected
 * <style>/<script> that makes each comment cell editable and bridges edits back
 * to the parent via postMessage. Cells are selected by data-remark-key, which the
 * backend emits deterministically from each cell's data-owner code.
 */
function buildInteractiveSrcDoc(html, readOnly) {
  const injected = `
<style>
  td.col-comments[data-remark-key] {
    position: relative;
    vertical-align: top !important;
    padding-top: 4px !important;
    text-align: left !important;
  }
  .remark-text {
    min-height: 26px;
    outline: none;
    padding-right: 22px;
    white-space: pre-wrap;
    cursor: text;
    text-align: left;
  }
  textarea.comments-box[data-remark-key="additional_comments"] {
    vertical-align: top !important;
    padding-top: 4px !important;
    padding-right: 28px !important;
    text-align: left !important;
  }
  .ai-comments-anchor { position: relative !important; }
  .remark-text:empty::before { content: 'Add comment…'; color: #bbb; }
  .ai-btn { font-size: 10px; line-height: 1; padding: 3px 6px; border: 1px solid #c9c9c9; border-radius: 5px; background: #fff; cursor: pointer; white-space: nowrap; }
  .ai-btn:hover { background: #2BB5D8; color: #fff; border-color: #2BB5D8; }
  td.col-comments[data-remark-key] > .ai-btn { position: absolute; top: 2px; right: 2px; }
  .ai-comments-anchor > .ai-box-btn { position: absolute !important; top: 2px; right: 2px; z-index: 2; }
</style>
<script>
(function () {
  var READ_ONLY = ${readOnly ? 'true' : 'false'};
  function post(m) { try { window.parent.postMessage(m, '*'); } catch (e) {} }

  function setupCell(cell) {
    if (cell.dataset.setup) return;
    cell.dataset.setup = '1';
    var key = cell.getAttribute('data-remark-key');
    var text = cell.textContent || '';
    cell.textContent = '';
    var div = document.createElement('div');
    div.className = 'remark-text';
    if (READ_ONLY) {
      div.textContent = text;
      cell.appendChild(div);
      return;
    }
    div.contentEditable = 'true';
    div.textContent = text;
    div.addEventListener('input', function () {
      post({ type: 'remark-change', key: key, value: div.textContent });
    });
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ai-btn';
    btn.title = 'Improve with AI';
    btn.textContent = '\\u2728';
    btn.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      post({ type: 'ai-improve', key: key });
    });
    cell.appendChild(div);
    cell.appendChild(btn);
  }

  document.querySelectorAll('td.col-comments[data-remark-key]').forEach(setupCell);

  var box = document.querySelector('textarea.comments-box[data-remark-key="additional_comments"]');
  if (box) {
    if (READ_ONLY) {
      box.setAttribute('readonly', 'readonly');
    } else {
      box.addEventListener('input', function () {
        post({ type: 'remark-change', key: 'additional_comments', value: box.value });
      });
      var anchor = box.parentElement || box;
      anchor.classList.add('ai-comments-anchor');
      var bbtn = document.createElement('button');
      bbtn.type = 'button';
      bbtn.className = 'ai-btn ai-box-btn';
      bbtn.title = 'Improve with AI';
      bbtn.textContent = '\\u2728';
      bbtn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        post({ type: 'ai-improve', key: 'additional_comments' });
      });
      anchor.appendChild(bbtn);
    }
  }

  window.addEventListener('message', function (ev) {
    var m = ev.data;
    if (!m || typeof m.type !== 'string' || m.type !== 'remark-set' || !m.key) return;
    if (m.key === 'additional_comments') {
      var b = document.querySelector('textarea.comments-box');
      if (b) b.value = m.value || '';
    } else {
      var cell = document.querySelector('td.col-comments[data-remark-key="' + m.key + '"]');
      if (cell) {
        var d = cell.querySelector('.remark-text');
        if (d) d.textContent = m.value || '';
      }
    }
  });

  function report() {
    var w = document.documentElement.scrollWidth || document.body.scrollWidth || 1047;
    var h = document.documentElement.scrollHeight || document.body.scrollHeight || 718;
    post({ type: 'card-size', width: w, height: h });
  }
  if (document.readyState === 'complete') report();
  else window.addEventListener('load', report);
  setTimeout(report, 250);
})();
</script>
`;

  if (html.indexOf('</body>') !== -1) {
    return html.replace('</body>', injected + '</body>');
  }
  return html + injected;
}

// ── Extracted: Preview Card Tab (interactive, scaled workspace) ────────────────
function PreviewCardTab({
  previewHtml, previewLoading, onRemarkChange, onImproveRemark, aiLoading,
  iframeRef, saving, saved, onSave, readOnly,
}) {
  const [natural, setNatural] = useState({ width: NATURAL_WIDTH, height: NATURAL_HEIGHT });
  const [avail, setAvail] = useState({ width: 0, height: 0 });
  const [viewportEl, setViewportEl] = useState(null);

  // srcDoc must stay stable across remark edits so the iframe is never reloaded
  // (which would drop focus). It only changes when the rendered HTML changes.
  const srcDoc = useMemo(() => buildInteractiveSrcDoc(previewHtml || '', readOnly), [previewHtml, readOnly]);

  // Measure the available viewport in BOTH dimensions: width = container
  // clientWidth, height = space from the container's top down to the bottom of the
  // window (small margin, floored to a usable minimum). The callback ref re-runs
  // this whenever the viewport mounts (after preview load / player switch), and the
  // ResizeObserver + window listener keep it fresh on layout shifts and resizes.
  useEffect(() => {
    if (!viewportEl) return;
    const measure = () => {
      const width = viewportEl.clientWidth;
      const top = viewportEl.getBoundingClientRect().top;
      const height = Math.max(240, window.innerHeight - top - 24);
      setAvail({ width, height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(viewportEl);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [viewportEl]);

  // Fit the whole A4 page in BOTH dimensions, capped at 1 (never upscale past the
  // natural size, which would blur text). scale stays 0 until first measurement.
  const scale = avail.width && avail.height && natural.width && natural.height
    ? Math.min(avail.width / natural.width, avail.height / natural.height, 1)
    : 0;
  const scaledW = natural.width * scale;
  const scaledH = natural.height * scale;
  // Center the scaled page within the available viewport.
  const offsetX = (avail.width - scaledW) / 2;
  const offsetY = (avail.height - scaledH) / 2;

  // Bridge: forward iframe postMessage events to the parent callbacks.
  useEffect(() => {
    const handler = (ev) => {
      const m = ev.data;
      if (!m || typeof m.type !== 'string') return;
      if (m.type === 'remark-change') {
        onRemarkChange(m.key, m.value);
      } else if (m.type === 'ai-improve') {
        onImproveRemark(m.key);
      } else if (m.type === 'card-size' && m.width && m.height) {
        setNatural(prev =>
          (prev.width === m.width && prev.height === m.height)
            ? prev
            : { width: m.width, height: m.height }
        );
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRemarkChange, onImproveRemark]);

  if (previewLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <Loader2 size={28} className="text-[#2BB5D8] animate-spin" />
        <p className="text-sm text-muted-foreground">Loading evaluation card…</p>
      </div>
    );
  }

  if (!previewHtml) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="p-4 rounded-full bg-[#2BB5D8]/10 border border-[#2BB5D8]/20">
          <Eye size={28} className="text-[#2BB5D8]" />
        </div>
        <p className="text-sm text-muted-foreground max-w-xs">
          Preview unavailable — ensure a template is assigned to this batch.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {readOnly
            ? 'View only — admins cannot edit evaluations. Fixes must be made from the coach\'s account.'
            : 'Edit comments directly on the card — click any comment cell to type.'}
        </p>
        {!readOnly && (
          <div className="flex items-center gap-2">
            {Object.values(aiLoading || {}).some(Boolean) && (
              <Loader2 size={12} className="animate-spin text-[#2BB5D8]" />
            )}
            <Button
              data-testid="save-remarks-btn"
              size="sm"
              className={saved
                ? 'bg-green-700 hover:bg-green-700 text-white font-semibold'
                : 'bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold'}
              onClick={onSave}
              disabled={saving}
            >
              {saving
                ? <Loader2 size={14} className="animate-spin mr-1" />
                : saved
                  ? <><CheckCircle size={14} className="mr-1" /> Saved!</>
                  : <><Save size={14} className="mr-1" /> Save Comments</>}
            </Button>
          </div>
        )}
      </div>
      <div
        ref={setViewportEl}
        className="rounded-xl overflow-hidden border border-border/60 bg-white w-full relative"
        style={{ height: avail.height }}
      >
        {scale > 0 && (
          <iframe
            ref={iframeRef}
            data-testid="preview-card-iframe"
            srcDoc={srcDoc}
            title="Evaluation Card Preview"
            style={{
              position: 'absolute',
              left: offsetX,
              top: offsetY,
              width: natural.width,
              height: natural.height,
              border: 'none',
              transform: `scale(${scale})`,
              transformOrigin: '0 0',
              display: 'block',
            }}
            sandbox="allow-same-origin allow-scripts"
          />
        )}
      </div>
    </div>
  );
}

// ── Extracted: PDF Generator Dialog ────────────────────────────────────────────
function PDFGeneratorDialog({ open, onClose, evaluation, selectedPlayer, onGenerate, pdfLoading }) {
  const players = evaluation?.players || [];
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#111827] border-border/60 max-w-md">
        <DialogHeader>
          <DialogTitle>Generate PDFs</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{players.length} players in this evaluation</p>
        <div className="space-y-3 mt-2">
          {[
            {
              type: 'individual',
              label: 'Selected Player PDF',
              sub: selectedPlayer?.name || 'No player selected',
              icon: Download,
              color: 'text-[#2BB5D8]',
            },
            {
              type: 'combined',
              label: 'Combined PDF',
              sub: `All ${players.length} players in one file`,
              icon: FileDown,
              color: 'text-[#F7941D]',
            },
            {
              type: 'zip',
              label: 'ZIP Archive',
              sub: 'Individual PDF for each player',
              icon: Package,
              color: 'text-green-400',
            },
          ].map(({ type, label, sub, icon: Icon, color }) => (
            <Button
              key={type}
              className="w-full justify-start gap-3 bg-secondary/50 border border-border/60 hover:bg-secondary text-foreground"
              onClick={() => onGenerate(type)}
              disabled={pdfLoading[type]}
            >
              {pdfLoading[type]
                ? <Loader2 size={16} className="animate-spin" />
                : <Icon size={16} className={color} />}
              <div className="text-left">
                <p className="font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">{sub}</p>
              </div>
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function EvaluationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [remarks, setRemarks] = useState({ additional_comments: '' });
  const [previewData, setPreviewData] = useState(null); // { html, commentCells, additionalComments }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [savingRemarks, setSavingRemarks] = useState(false);
  const [remarkSaved, setRemarkSaved] = useState(false);
  const [aiLoading, setAiLoading] = useState({});
  const [improveAllProgress, setImproveAllProgress] = useState(null); // { done, total } while running
  const [pdfLoading, setPdfLoading] = useState({});
  const [pdfDialog, setPdfDialog] = useState(false);
  const [error, setError] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const iframeRef = useRef(null);
  // Bumped whenever an "Improve all" run starts or the player changes, so a run
  // that is still in flight never writes into another player's card.
  const improveRunRef = useRef(0);
  // Comments as first loaded for the selected player (gender is detected from these).
  const originalRemarksRef = useRef({});
  // Persisted AI state of the whole evaluation (GET /improve-plan): every non-empty
  // comment box with status improved | failed | pending. It lives in the database, so
  // failures are still listed after logging out and back in.
  const [plan, setPlan] = useState(null);
  const [bulk, setBulk] = useState(null); // { done, total, label } while a whole-evaluation run is going
  const [showFailures, setShowFailures] = useState(false);
  const bulkRunRef = useRef(0);

  // BACKEND_URL, axios, all set* are stable module/useState refs.
  // Only `id` is volatile — this is the correct deps array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/evaluations/${id}`);
      setEvaluation(res.data);
      setTitleDraft(res.data.title || '');
      if (res.data.players?.length > 0) {
        setSelectedPlayer(res.data.players[0]);
      }
    } catch {
      setError('Failed to load evaluation');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const loadPlan = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/evaluations/${id}/improve-plan`);
      setPlan(res.data);
    } catch {
      // The panel is an enhancement; the card-level tools keep working without it.
    }
  }, [id]);

  useEffect(() => {
    if (!isAdmin) loadPlan();
    return () => { bulkRunRef.current += 1; }; // leaving the page stops a run in flight
  }, [loadPlan, isAdmin]);

  // Failed boxes of the open card, from the persisted plan.
  const failedKeys = useMemo(() => {
    const p = plan?.players.find(x => x.player_id === selectedPlayer?.player_id);
    return (p?.items || []).filter(i => i.status === 'failed').map(i => i.key);
  }, [plan, selectedPlayer?.player_id]);

  // Record one attempt (improved text and/or failure) so it survives a reload.
  const persistAiResult = useCallback(async (playerId, body) => {
    try {
      await axios.put(`${BACKEND_URL}/api/evaluations/${id}/players/${playerId}/ai-result`, body);
      return true;
    } catch {
      return false;
    }
  }, [id]);

  // Load the rendered card + deterministic comment structure for a player, and
  // build the working remarks object from it (canonical code keys + additional).
  const loadPreviewForPlayer = useCallback(async (player) => {
    if (!player) return;
    setPreviewLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/evaluations/${id}/players/${player.player_id}/preview-html`
      );
      const commentCells = res.data.comment_cells || [];
      const additional = res.data.additional_comments || '';
      const next = { additional_comments: additional };
      for (const c of commentCells) next[c.key] = c.value || '';
      // Preserve any extra keys the player carries (e.g. legacy offense_remark).
      if (player.remarks) {
        for (const [k, v] of Object.entries(player.remarks)) {
          if (!(k in next)) next[k] = v || '';
        }
      }
      setPreviewData({
        html: res.data.html || '',
        commentCells,
        additionalComments: additional,
      });
      originalRemarksRef.current = { ...next };
      setRemarks(next);
      setRemarkSaved(false);
      setAiLoading({});
    } catch {
      setError('Failed to load preview. Ensure a template is assigned to this batch.');
    } finally {
      setPreviewLoading(false);
    }
  }, [id]);

  useEffect(() => {
    improveRunRef.current += 1; // cancel any in-flight "Improve all" for the previous player
    setImproveAllProgress(null);
    if (selectedPlayer) loadPreviewForPlayer(selectedPlayer);
    // Only re-run when the selected player changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayer?.player_id]);

  const selectPlayer = (player) => {
    setSelectedPlayer(player);
    setRemarkSaved(false);
  };

  const handleRemarksChange = useCallback((key, value) => {
    setRemarks(p => ({ ...p, [key]: value }));
  }, []);

  const handleSaveRemarks = async () => {
    if (!selectedPlayer) return;
    setSavingRemarks(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/evaluations/${id}/players/${selectedPlayer.player_id}/remarks`,
        remarks
      );
      setEvaluation(prev => ({
        ...prev,
        players: prev.players.map(p =>
          p.player_id === selectedPlayer.player_id ? { ...p, remarks } : p
        ),
      }));
      setSelectedPlayer(prev => ({ ...prev, remarks }));
      setRemarkSaved(true);
      setTimeout(() => setRemarkSaved(false), 2000);
      loadPlan(); // hand-edited boxes drop their AI status on the server
    } catch {
      setError('Failed to save remarks');
    } finally {
      setSavingRemarks(false);
    }
  };

  // Improve a single remark with AI, sending full context (player, batch, section,
  // subsection, parameters+scores). The result is posted back into the same cell.
  const handleImproveRemark = useCallback(async (key) => {
    const current = remarks[key];
    if (!current?.trim()) return;
    const cell = (previewData?.commentCells || []).find(c => c.key === key);
    setAiLoading(p => ({ ...p, [key]: true }));
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/ai/improve-remark`,
        buildImprovePayload(
          current, cell, selectedPlayer, evaluation,
          collectCardRemarks(originalRemarksRef.current, remarks)
        )
      );
      if (res.data.ok === false) {
        setError(
          `AI improvement failed${res.data.reason ? ` (${res.data.reason})` : ''}. Your text was kept.`
        );
        return;
      }
      const improved = res.data.improved_remark;
      setRemarks(p => ({ ...p, [key]: improved }));
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'remark-set', key, value: improved },
        '*'
      );
    } catch {
      setError('AI improvement failed. Check your remark and try again.');
    } finally {
      setAiLoading(p => ({ ...p, [key]: false }));
    }
  }, [remarks, previewData, selectedPlayer, evaluation]);

  // Improve every non-empty comment box on the current card (section/subsection
  // boxes plus Additional Comments) in one click. Results land in the boxes and in
  // `remarks` only — nothing is saved and no PDF is generated; the coach reviews and
  // uses "Save Comments" as usual.
  const handleImproveAll = useCallback(async () => {
    const cells = previewData?.commentCells || [];
    const filled = [...new Set([...cells.map(c => c.key), 'additional_comments'])]
      .filter(k => (remarks[k] || '').trim());
    // After a partial failure the button retries just the failed boxes, so boxes that
    // already succeeded are not polished a second time. The failures come from the
    // persisted plan, so they are remembered across reloads and logins.
    const retryKeys = failedKeys.filter(k => filled.includes(k));
    const keys = retryKeys.length ? retryKeys : filled;
    if (!keys.length) return;

    const runId = ++improveRunRef.current;
    const isStale = () => improveRunRef.current !== runId;
    const snapshot = { ...remarks };
    const cardRemarks = collectCardRemarks(originalRemarksRef.current, snapshot);
    const total = keys.length;
    const queue = [...keys];
    const failedNow = [];
    let done = 0;
    let improvedCount = 0;
    let failReason = '';

    setError('');
    setImproveAllProgress({ done: 0, total });

    const worker = async () => {
      while (queue.length && !isStale()) {
        const key = queue.shift();
        const cell = cells.find(c => c.key === key);
        setAiLoading(p => ({ ...p, [key]: true }));
        const { improved, reason } = await requestImprove(
          buildImprovePayload(snapshot[key], cell, selectedPlayer, evaluation, cardRemarks),
          isStale
        );
        if (isStale()) return;
        const playerId = selectedPlayer.player_id;
        if (improved) {
          improvedCount += 1;
          setRemarks(p => ({ ...p, [key]: improved }));
          iframeRef.current?.contentWindow?.postMessage(
            { type: 'remark-set', key, value: improved },
            '*'
          );
          persistAiResult(playerId, { key, status: 'improved', improved_remark: improved });
        } else {
          failedNow.push(key);
          failReason = failReason || reason;
          persistAiResult(playerId, { key, status: 'failed', reason });
        }
        setAiLoading(p => ({ ...p, [key]: false }));
        done += 1;
        setImproveAllProgress({ done, total });
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(IMPROVE_ALL_CONCURRENCY, total) }, worker)
    );
    if (isStale()) return;
    setImproveAllProgress(null);
    loadPlan();
    if (failedNow.length) {
      setError(
        `Improved ${improvedCount} of ${total} comments. ` +
        `Could not improve: ${failedNow.map(k => boxLabel(k, cells)).join(', ')}` +
        `${failReason ? ` (${failReason})` : ''}. Their original text was kept — ` +
        `click "Retry ${failedNow.length} failed" to try again.`
      );
    }
  }, [remarks, previewData, selectedPlayer, evaluation, failedKeys, persistAiResult, loadPlan]);

  // Improve every comment of every player that is not already improved (failed ones
  // and ones never attempted, e.g. after an interrupted run). `only` limits the run to
  // [{ playerId, key }] for a single-box retry. Each result is saved as it arrives, so
  // closing the tab loses nothing and the next run picks up where this one stopped.
  const runBulk = useCallback(async (only) => {
    if (!plan) return;
    const queue = [];
    for (const p of plan.players) {
      for (const item of p.items) {
        if (item.status === 'improved') continue;
        if (only && !only.some(o => o.playerId === p.player_id && o.key === item.key)) continue;
        // The open card may hold edits that are not saved yet; those win.
        const live = selectedPlayer?.player_id === p.player_id ? (remarks[item.key] || '').trim() : '';
        queue.push({
          player: p,
          item: live ? { ...item, text: live } : item,
          cardRemarks: selectedPlayer?.player_id === p.player_id
            ? collectCardRemarks(originalRemarksRef.current, remarks)
            : p.card_remarks,
        });
      }
    }
    if (!queue.length) return;

    const runId = ++bulkRunRef.current;
    const isStale = () => bulkRunRef.current !== runId;
    const total = queue.length;
    let done = 0;
    let failed = 0;
    let notConfigured = '';
    setError('');
    setBulk({ done: 0, total, label: '' });

    const worker = async () => {
      while (queue.length && !isStale() && !notConfigured) {
        const { player, item, cardRemarks } = queue.shift();
        setBulk(b => b && { ...b, label: `${player.name} – ${item.label || item.key}` });
        const { improved, reason } = await requestImprove(
          buildBulkPayload(item, player.name, evaluation, cardRemarks), isStale
        );
        if (isStale()) return;
        if (!improved && /not configured/i.test(reason || '')) {
          // Nothing is wrong with the comments: don't mark hundreds of boxes as failed.
          notConfigured = reason;
          return;
        }
        let ok = !!improved;
        if (improved) {
          ok = await persistAiResult(player.player_id, {
            key: item.key, status: 'improved', improved_remark: improved,
          });
          if (!ok) {
            await persistAiResult(player.player_id, {
              key: item.key, status: 'failed', reason: 'Improved text could not be saved',
            });
          }
        } else {
          await persistAiResult(player.player_id, { key: item.key, status: 'failed', reason });
        }
        if (isStale()) return;
        if (ok) {
          if (selectedPlayer?.player_id === player.player_id) {
            setRemarks(p => ({ ...p, [item.key]: improved }));
            iframeRef.current?.contentWindow?.postMessage(
              { type: 'remark-set', key: item.key, value: improved }, '*'
            );
          }
          setEvaluation(prev => prev && ({
            ...prev,
            players: prev.players.map(pl => pl.player_id === player.player_id
              ? { ...pl, remarks: { ...(pl.remarks || {}), [item.key]: improved } }
              : pl),
          }));
        } else {
          failed += 1;
        }
        done += 1;
        setBulk(b => b && { ...b, done });
      }
    };

    await Promise.all(Array.from({ length: Math.min(IMPROVE_ALL_CONCURRENCY, total) }, worker));
    if (isStale()) return;
    setBulk(null);
    await loadPlan();
    if (notConfigured) {
      setError(`${notConfigured}. No comments were changed.`);
    } else if (failed) {
      setError(`${failed} comment${failed === 1 ? '' : 's'} could not be improved. See the list below to retry them.`);
      setShowFailures(true);
    }
  }, [plan, selectedPlayer, remarks, evaluation, persistAiResult, loadPlan]);

  const cancelBulk = () => {
    bulkRunRef.current += 1;
    setBulk(null);
    loadPlan();
  };

  const handleGeneratePDF = async (type) => {
    setPdfLoading(p => ({ ...p, [type]: true }));
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/evaluations/${id}/generate-pdf`,
        {
          pdf_type: type,
          player_ids: type === 'individual' && selectedPlayer ? [selectedPlayer.player_id] : null,
        },
        { responseType: 'blob', timeout: 120000 }
      );
      const ext = type === 'zip' ? 'zip' : 'pdf';
      const mime = type === 'zip' ? 'application/zip' : 'application/pdf';
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      let baseName;
      if (type === 'individual') {
        baseName = `${evaluation.batch_name || 'Batch'}_${selectedPlayer?.name || 'Player'}`;
      } else {
        baseName = `${evaluation.cycle_name || 'evaluation'}_${type}`;
      }
      a.download = `${baseName.replace(/ /g, '_')}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      setPdfDialog(false);
    } catch (e) {
      setError(`PDF generation failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPdfLoading(p => ({ ...p, [type]: false }));
    }
  };

  const handleSubmitEvaluation = async () => {
    const title = titleDraft.trim();
    if (!title) {
      setError('Please enter a name for this evaluation before submitting');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/evaluations/${id}/submit`, { title });
      setEvaluation(prev => ({ ...prev, title: res.data.title, status: res.data.status, submitted_at: res.data.submitted_at }));
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to submit evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-3">
          {[1, 2, 3].map(i => (
            <div key={`skel-${i}`} className="h-20 bg-[#111827] rounded-xl animate-pulse" />
          ))}
        </div>
      </Layout>
    );
  }

  if (!evaluation) {
    return (
      <Layout>
        <div className="p-6 text-center text-muted-foreground">Evaluation not found</div>
      </Layout>
    );
  }

  const players = evaluation.players || [];
  const improvableCount = [
    ...(previewData?.commentCells || []).map(c => c.key),
    'additional_comments',
  ].filter((k, i, a) => a.indexOf(k) === i && (remarks[k] || '').trim()).length;
  const playerFailed = (playerId) =>
    plan?.players.find(p => p.player_id === playerId)?.items.filter(i => i.status === 'failed').length || 0;
  const isSubmitted = evaluation.status === 'submitted';
  const downloadBlocked = isAdmin && !isSubmitted;

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold">{evaluation.title || evaluation.cycle_name}</h1>
              <Badge
                variant="outline"
                className={isSubmitted
                  ? 'text-[10px] border-green-500/30 text-green-400'
                  : 'text-[10px] border-yellow-500/30 text-yellow-400'}
              >
                {isSubmitted ? 'Submitted' : 'In Progress'}
              </Badge>
              {isAdmin && (
                <Badge variant="outline" className="text-[10px] border-border/60 text-muted-foreground gap-1">
                  <Lock size={10} /> View only
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {evaluation.coach_name && `${evaluation.coach_name} · `}
              {evaluation.batch_name} · {evaluation.location_name} · {evaluation.evaluation_date}
            </p>
            {evaluation.template_version && (
              <p className="text-[10px] text-muted-foreground/70">
                Template v{evaluation.template_version}
              </p>
            )}
          </div>
          <Button
            data-testid="generate-pdf-btn"
            onClick={() => setPdfDialog(true)}
            disabled={downloadBlocked}
            title={downloadBlocked ? "Available once the coach submits this evaluation" : undefined}
            className="bg-[#F7941D] hover:bg-[#e08519] text-white font-semibold"
          >
            <FileDown size={16} className="mr-1" /> Generate PDFs
          </Button>
        </div>

        {!isAdmin && (
          <Card className="bg-[#111827] border-border/60">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-semibold">
                  {isSubmitted ? 'Submitted evaluation name' : 'Name this evaluation to submit it'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isSubmitted
                    ? `Submitted ${evaluation.submitted_at ? new Date(evaluation.submitted_at).toLocaleString() : ''}. You can keep editing and resubmit if anything needs fixing.`
                    : 'A few words is enough — this is what the admin will see once you submit.'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="evaluation-title-input"
                  placeholder="e.g. March Cycle – U14 Batch A"
                  value={titleDraft}
                  onChange={e => setTitleDraft(e.target.value)}
                  maxLength={100}
                  className="bg-secondary/40 border-border/60 h-9 w-64"
                />
                <Button
                  data-testid="submit-evaluation-btn"
                  size="sm"
                  onClick={handleSubmitEvaluation}
                  disabled={submitting || !titleDraft.trim()}
                  className={submitted
                    ? 'bg-green-700 hover:bg-green-700 text-white font-semibold'
                    : 'bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold'}
                >
                  {submitting
                    ? <Loader2 size={14} className="animate-spin mr-1" />
                    : submitted
                      ? <><CheckCircle size={14} className="mr-1" /> Submitted!</>
                      : <><Send size={14} className="mr-1" /> {isSubmitted ? 'Resubmit' : 'Submit Evaluation'}</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!isAdmin && plan && plan.counts.total > 0 && (
          <Card className="bg-[#111827] border-border/60" data-testid="bulk-improve-panel">
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">Improve comments with AI — whole evaluation</p>
                  <p className="text-xs text-muted-foreground">
                    {plan.counts.total} comments · {plan.counts.improved} improved
                    {plan.counts.failed > 0 && <> · <span className="text-red-400">{plan.counts.failed} failed</span></>}
                    {plan.counts.pending > 0 && <> · {plan.counts.pending} not improved yet</>}
                    . Each result is saved as it arrives; only failed or remaining comments are retried.
                  </p>
                  {bulk && bulk.label && (
                    <p className="text-xs text-[#2BB5D8] truncate mt-1">Improving: {bulk.label}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {bulk && (
                    <Button size="sm" variant="ghost" onClick={cancelBulk} data-testid="bulk-cancel-btn">
                      Stop
                    </Button>
                  )}
                  <Button
                    data-testid="bulk-improve-btn"
                    size="sm" variant="outline"
                    className="border-[#2BB5D8]/30 text-[#2BB5D8] hover:bg-[#2BB5D8]/10"
                    onClick={() => runBulk()}
                    disabled={!!bulk || !!improveAllProgress || previewLoading
                      || plan.counts.failed + plan.counts.pending === 0}
                  >
                    {bulk
                      ? <Loader2 size={13} className="animate-spin mr-1" />
                      : <Sparkles size={13} className="mr-1" />}
                    {bulk
                      ? `Improving ${bulk.done}/${bulk.total}…`
                      : plan.counts.failed + plan.counts.pending === 0
                        ? 'All comments improved'
                        : plan.counts.failed > 0
                          ? `Retry ${plan.counts.failed} failed${plan.counts.pending ? ` + ${plan.counts.pending} left` : ''}`
                          : plan.counts.improved > 0
                            ? `Continue (${plan.counts.pending} left)`
                            : 'Improve all comments'}
                  </Button>
                </div>
              </div>

              {plan.counts.failed > 0 && (
                <div>
                  <button
                    type="button"
                    className="text-xs text-red-400 underline"
                    onClick={() => setShowFailures(s => !s)}
                  >
                    {showFailures ? 'Hide' : 'Show'} the {plan.counts.failed} failed comment{plan.counts.failed === 1 ? '' : 's'}
                  </button>
                  {showFailures && (
                    <ul className="mt-2 space-y-1 max-h-60 overflow-y-auto" data-testid="bulk-failed-list">
                      {plan.players.flatMap(p => p.items
                        .filter(i => i.status === 'failed')
                        .map(i => (
                          <li
                            key={`${p.player_id}:${i.key}`}
                            className="flex items-center justify-between gap-2 text-xs rounded-lg bg-red-500/5 border border-red-500/20 px-3 py-1.5"
                          >
                            <span className="min-w-0">
                              <span className="font-medium">{p.name}</span>
                              <span className="text-muted-foreground"> – {[i.section, i.subsection].filter(Boolean).join(' – ') || i.label}</span>
                              {i.reason && <span className="text-red-400/80"> ({i.reason})</span>}
                            </span>
                            <Button
                              size="sm" variant="ghost" className="h-6 px-2 text-[#2BB5D8]"
                              disabled={!!bulk}
                              onClick={() => runBulk([{ playerId: p.player_id, key: i.key }])}
                            >
                              Retry
                            </Button>
                          </li>
                        )))}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid lg:grid-cols-[280px_1fr] gap-4">
          {/* Player List */}
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
              Players ({players.length})
            </p>
            <div className="space-y-1 max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
              {players.map((player, i) => {
                const scores = Object.values(player.scores || {}).filter(v => v != null);
                const avg = scores.length
                  ? scores.reduce((a, b) => a + Number(b), 0) / scores.length
                  : null;
                const hasRemarks = Object.values(player.remarks || {}).some(r => r);
                const isSelected = selectedPlayer?.player_id === player.player_id;
                return (
                  <button
                    key={player.player_id}
                    data-testid={`player-item-${player.player_id}`}
                    onClick={() => selectPlayer(player)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-colors ${
                      isSelected ? 'nav-active' : 'hover:bg-secondary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                      <div>
                        <p className="text-sm font-medium leading-none">{player.name}</p>
                        {playerFailed(player.player_id) > 0 ? (
                          <span className="text-[10px] text-red-400">
                            {playerFailed(player.player_id)} AI failed
                          </span>
                        ) : hasRemarks && (
                          <span className="text-[10px] text-green-400">Remarks added</span>
                        )}
                      </div>
                    </div>
                    {avg !== null && <ScoreBadge score={avg} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Player Detail */}
          {selectedPlayer && (
            <Card className="bg-[#111827] border-border/60 overflow-hidden min-w-0">
              <CardHeader className="pb-3 border-b border-border/60">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{selectedPlayer.name}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {evaluation.batch_name} · {evaluation.cycle_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isAdmin && (
                      <Button
                        data-testid="improve-all-btn"
                        size="sm" variant="outline"
                        className="border-[#2BB5D8]/30 text-[#2BB5D8] hover:bg-[#2BB5D8]/10"
                        onClick={handleImproveAll}
                        disabled={!!improveAllProgress || !!bulk || previewLoading || improvableCount === 0}
                        title="Improve every filled comment box on this card with AI"
                      >
                        {improveAllProgress
                          ? <Loader2 size={13} className="animate-spin mr-1" />
                          : <Sparkles size={13} className="mr-1" />}
                        {improveAllProgress
                          ? `Improving ${improveAllProgress.done}/${improveAllProgress.total}…`
                          : failedKeys.length
                            ? `Retry ${failedKeys.length} failed`
                            : 'Improve all comments'}
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline"
                      className="border-[#F7941D]/30 text-[#F7941D] hover:bg-[#F7941D]/10"
                      onClick={() => handleGeneratePDF('individual')}
                      disabled={pdfLoading.individual || downloadBlocked}
                      title={downloadBlocked ? "Available once the coach submits this evaluation" : undefined}
                    >
                      {pdfLoading.individual
                        ? <Loader2 size={13} className="animate-spin" />
                        : <Download size={13} className="mr-1" />}
                      PDF
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-4">
                <Tabs defaultValue="preview">
                  <TabsList className="bg-secondary/50 mb-4">
                    <TabsTrigger value="preview" data-testid="preview-tab">
                      <Eye size={13} className="mr-1" /> Preview Card
                    </TabsTrigger>
                    <TabsTrigger value="scores">Scores</TabsTrigger>
                  </TabsList>

                  {/* Preview Card Tab — the editing workspace */}
                  <TabsContent value="preview">
                    <PreviewCardTab
                      previewHtml={previewData?.html || ''}
                      previewLoading={previewLoading}
                      onRemarkChange={handleRemarksChange}
                      onImproveRemark={handleImproveRemark}
                      aiLoading={aiLoading}
                      iframeRef={iframeRef}
                      saving={savingRemarks}
                      saved={remarkSaved}
                      onSave={handleSaveRemarks}
                      readOnly={isAdmin}
                    />
                  </TabsContent>

                  {/* Scores Tab */}
                  <TabsContent value="scores">
                    <div className="space-y-4">
                      {/* Use backend-provided category_groups for dynamic categorization */}
                      {selectedPlayer.category_groups ? (
                        Object.entries(selectedPlayer.category_groups).map(([cat, skills]) => (
                          <div key={cat}>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                              {CATEGORY_LABELS[cat] || cat}
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {skills.map(({ key, label, score }) => (
                                <div
                                  key={key}
                                  className="flex items-center justify-between px-3 py-2 bg-secondary/40 rounded-lg"
                                >
                                  <span className="text-xs text-muted-foreground truncate mr-2" title={label}>
                                    {label}
                                  </span>
                                  <ScoreBadge score={score} />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        // Fallback for evaluations created before category_groups was added
                        Object.entries(selectedPlayer.scores || {}).map(([skill, val]) => (
                          <div key={skill} className="flex items-center justify-between px-3 py-2 bg-secondary/40 rounded-lg">
                            <span className="text-xs text-muted-foreground truncate mr-2" title={skill}>
                              {skill}
                            </span>
                            <ScoreBadge score={val} />
                          </div>
                        ))
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>

        <PDFGeneratorDialog
          open={pdfDialog}
          onClose={() => setPdfDialog(false)}
          evaluation={evaluation}
          selectedPlayer={selectedPlayer}
          onGenerate={handleGeneratePDF}
          pdfLoading={pdfLoading}
        />
      </div>
    </Layout>
  );
}
